import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { encodeUrlSearchParams } from '@round/api';
import { Primitives } from '../utils/utils.types';
import { parseUrlSearchParams } from '../utils/utits';

type AcceptedArrayTypes = number[] | string[] | boolean[];
type UrlState = { [key: string]: Primitives | AcceptedArrayTypes };

const parsers: { [index: string]: (value: string) => Primitives | AcceptedArrayTypes } = {
    boolean: (value: string) => (value === 'true' ? true : false),
    number: (value: string) => Number(value),
    'array-number': (value: string) => value.split(',').map((v) => Number(v)),
    'array-string': (value: string) => value.split(','),
    'array-boolean': (value: string) => value.split(',').map((v) => (v === 'true' ? true : false)),
};

const serializers: { [index: string]: (value: any) => string } = {
    boolean: (value: boolean) => String(value),
    number: (value: number) => String(value),
    'array-number': (value: AcceptedArrayTypes) => value.join(','),
    'array-string': (value: AcceptedArrayTypes) => value.join(','),
    'array-boolean': (value: AcceptedArrayTypes) => value.join(','),
};

type TypeModifier = 'boolean' | 'number' | 'array-number' | 'array-string' | 'array-boolean';
const isTypeModifierString = (value: string | object | undefined): value is TypeModifier => {
    return (
        value === 'boolean' ||
        value === 'number' ||
        value === 'array-number' ||
        value === 'array-string' ||
        value === 'array-boolean'
    );
};

export default function useUrlState<T extends UrlState>(
    initialState?: Partial<T>,
    config?: {
        [key in keyof T]?:
            | { parser?: (value: string) => T[key]; serializer?: (value: T[key]) => string }
            | TypeModifier;
    }
): [Partial<T>, (s: React.SetStateAction<Partial<T>>, replace?: boolean) => void] {
    const mounted = useRef(false);
    const location = useLocation();
    const navigate = useNavigate();

    const [, setUpdated] = useState(false);
    const queryFromUrl = useMemo(() => parseUrlSearchParams(location.search), [location.search]);

    const queryParsedToTypes: Partial<T> = useMemo(() => {
        const parsedQuery = {};
        for (const key in queryFromUrl) {
            const keyConfig = config?.[key];
            let parser;

            if (isTypeModifierString(keyConfig)) {
                parser = parsers[keyConfig];
            } else {
                parser = keyConfig?.parser;
            }

            if (!parser) {
                Object.assign(parsedQuery, { [key]: queryFromUrl[key] });
                continue;
            }

            Object.assign(parsedQuery, { [key]: parser(queryFromUrl[key]) });
        }
        return parsedQuery;
    }, [queryFromUrl, config]);

    const setState = useCallback(
        (s: React.SetStateAction<Partial<T>>, replace?: boolean) => {
            const newQuery = typeof s === 'function' ? (s as Function)(queryFromUrl) : s;
            const newQueryWithSerializedValues = {};

            for (const key in newQuery) {
                const keyConfig = config?.[key];
                const value = newQuery[key];
                let serializer;

                if (isTypeModifierString(keyConfig)) {
                    serializer = serializers[keyConfig];
                } else {
                    serializer = keyConfig?.serializer;
                }

                if (!keyConfig || !serializer || value === undefined || value === null) {
                    Object.assign(newQueryWithSerializedValues, { [key]: value });
                    continue;
                }

                Object.assign(newQueryWithSerializedValues, { [key]: serializer(value) });
            }

            setUpdated((v) => !v);
            navigate(
                {
                    hash: location.hash,
                    search: encodeUrlSearchParams(
                        replace
                            ? { ...newQueryWithSerializedValues }
                            : { ...queryFromUrl, ...newQueryWithSerializedValues }
                    ),
                },
                { replace: true }
            );
        },
        [location.hash, navigate, queryFromUrl, config]
    );

    useEffect(() => {
        const nonEmptyInitialStateValues = Object.values(initialState ?? {}).filter((v) => !!v).length;
        if (!location.search && initialState && nonEmptyInitialStateValues && !mounted.current) {
            setState(initialState);
        }
    }, [initialState, location.search, setState]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return [queryParsedToTypes, setState];
}
